import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface LogoProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  to?: string;
  /** Show only the mark (no wordmark) */
  markOnly?: boolean;
}

const Logo = ({ className, size = "md", to, markOnly = false }: LogoProps) => {
  const wordSizeClass = {
    sm: "text-[17px]",
    md: "text-2xl",
    lg: "text-3xl",
  }[size];

  const markSize = {
    sm: "h-7 w-7",
    md: "h-9 w-9",
    lg: "h-11 w-11",
  }[size];

  const Mark = (
    <svg
      viewBox="0 0 40 40"
      className={cn(markSize, "flex-shrink-0")}
      aria-hidden="true"
      fill="none"
    >
      {/* Soft outer halo for depth */}
      <circle cx="20" cy="20" r="19.5" fill="hsl(var(--primary) / 0.08)" />
      {/* Primary disc */}
      <circle cx="20" cy="20" r="17" fill="hsl(var(--primary))" />
      {/* Inner highlight ring */}
      <circle
        cx="20"
        cy="20"
        r="13.5"
        stroke="hsl(var(--primary-foreground) / 0.18)"
        strokeWidth="0.75"
      />
      {/* Sans-serif S monogram, reversed out */}
      <text
        x="20"
        y="26.5"
        textAnchor="middle"
        fontFamily="'Instrument Sans', system-ui, sans-serif"
        fontSize="20"
        fontWeight="600"
        letterSpacing="-0.02em"
        fill="hsl(var(--primary-foreground))"
      >
        S
      </text>
    </svg>
  );

  const content = (
    <span className="inline-flex items-center gap-2.5">
      {Mark}
      {!markOnly && (
        <span
          className={cn(
            "font-sans font-medium tracking-[-0.02em] text-foreground leading-none",
            wordSizeClass
          )}
        >
          Send<span className="text-foreground/55 font-normal">feedback</span>
        </span>
      )}
    </span>
  );

  if (to) {
    return (
      <Link
        to={to}
        aria-label="SendFeedback home"
        className={cn(
          "inline-flex items-center transition-opacity duration-200 hover:opacity-85",
          className
        )}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={cn("inline-flex items-center", className)} aria-label="SendFeedback">
      {content}
    </div>
  );
};

export default Logo;
